import { useNavigate } from 'react-router';
import { ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn, initials } from '@/lib/utils';
import { SourceBadge } from './source-badge';
import { StatusBadge } from './status-badge';
import type { AccountListItem } from '@/types';

interface Props {
  accounts: AccountListItem[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const activeFeatures = (a: AccountListItem) => {
  const labels: string[] = [];
  if (a.featureGuards?.v3 || a.featureGuards?.v3_shadow) labels.push('AI');
  if (a.featureGuards?.shadow_mode_enabled) labels.push('Shadow');
  return labels;
};

const HEAD = 'h-10 text-xs font-medium uppercase tracking-wide text-muted-foreground';

export function AccountsTable({
  accounts,
  isLoading,
  page,
  totalPages,
  onPageChange
}: Props) {
  const navigate = useNavigate();

  return (
    <div className='space-y-4'>
      <div className='overflow-hidden rounded-xl border bg-card shadow-sm'>
        <Table className='text-sm'>
          <TableHeader>
            <TableRow className='border-b bg-muted/30 hover:bg-muted/30'>
              <TableHead className={cn(HEAD, 'pl-4')}>Account</TableHead>
              <TableHead className={cn(HEAD, 'w-[140px]')}>Where it lives</TableHead>
              <TableHead className={cn(HEAD, 'w-[120px]')}>Status</TableHead>
              <TableHead className={cn(HEAD, 'w-[180px]')}>AI features</TableHead>
              <TableHead className={cn(HEAD, 'w-[90px] text-right')}>Users</TableHead>
              <TableHead className={cn(HEAD, 'w-[44px] pr-3')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i} className='border-b hover:bg-transparent'>
                  <TableCell className='py-2.5 pl-4'>
                    <div className='flex items-center gap-3'>
                      <Skeleton className='h-8 w-8 shrink-0 rounded-md' />
                      <div className='space-y-1.5'>
                        <Skeleton className='h-3 w-28' />
                        <Skeleton className='h-2.5 w-20' />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Skeleton className='h-4 w-20 rounded-full' />
                  </TableCell>
                  <TableCell>
                    <Skeleton className='h-4 w-16 rounded-full' />
                  </TableCell>
                  <TableCell>
                    <Skeleton className='h-3 w-10' />
                  </TableCell>
                  <TableCell>
                    <Skeleton className='ml-auto h-3 w-6' />
                  </TableCell>
                  <TableCell className='pr-3' />
                </TableRow>
              ))
            ) : accounts.length === 0 ? (
              <TableRow className='hover:bg-transparent'>
                <TableCell
                  colSpan={6}
                  className='py-16 text-center text-sm text-muted-foreground'
                >
                  No accounts found. Try adjusting your search or filters.
                </TableCell>
              </TableRow>
            ) : (
              accounts.map((a, idx) => {
                const clickable = a.id != null;
                const features = activeFeatures(a);
                return (
                  <TableRow
                    key={a.id ?? `mongo-${a.name}-${idx}`}
                    className={cn(
                      'group border-b transition-colors',
                      clickable
                        ? 'cursor-pointer hover:bg-muted/40'
                        : 'opacity-70'
                    )}
                    onClick={() => clickable && navigate(`/accounts/${a.id}`)}
                  >
                    <TableCell className='py-2.5 pl-4'>
                      <div className='flex items-center gap-3'>
                        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[11px] font-semibold text-primary'>
                          {initials(a.name)}
                        </div>
                        <div className='min-w-0'>
                          <p className='truncate text-sm font-medium leading-tight'>
                            {a.name ?? 'Unnamed account'}
                          </p>
                          {a.sub_domain && (
                            <p className='truncate text-xs leading-tight text-muted-foreground'>
                              {a.sub_domain}.kim.cc
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <SourceBadge source={a.source} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={a.account_status} />
                    </TableCell>
                    <TableCell>
                      <div className='flex flex-wrap gap-1'>
                        {features.length > 0 ? (
                          features.map((f) => (
                            <Badge
                              key={f}
                              variant='outline'
                              className='font-normal'
                            >
                              {f}
                            </Badge>
                          ))
                        ) : (
                          <span className='text-xs text-muted-foreground'>
                            None
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='text-right tabular-nums text-muted-foreground'>
                      {a.userCount ?? 0}
                    </TableCell>
                    <TableCell className='pr-3'>
                      {clickable && (
                        <ChevronRight className='h-4 w-4 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground' />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className='flex items-center justify-between px-1'>
        <p className='text-xs text-muted-foreground'>
          Page {page} of {totalPages}
        </p>
        <div className='flex gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
